import { useState } from "react";
import { Btn } from "./ui/Button";
import { Card } from "./ui/Card";
import { Input } from "./ui/Input";

interface EditFieldDef {
  key: string;
  label: string;
  type?: "text" | "number" | "date";
}

interface EditOptions {
  title: string;
  fields: EditFieldDef[];
  initialValues: object;
}

interface EditState extends EditOptions {
  resolve: (value: Record<string, string> | null) => void;
}

export function useEditDialog() {
  const [state, setState] = useState<EditState | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});

  const edit = (options: EditOptions) =>
    new Promise<Record<string, string> | null>((resolve) => {
      const initialValues = options.initialValues as Record<string, unknown>;
      const initial: Record<string, string> = {};
      for (const f of options.fields) {
        const v = initialValues[f.key];
        initial[f.key] = v === null || v === undefined ? "" : String(v);
      }
      setValues(initial);
      setState({ ...options, resolve });
    });

  const close = (result: Record<string, string> | null) => {
    state?.resolve(result);
    setState(null);
  };

  const dialog = state && (
    <div
      className="fixed inset-0 flex items-center justify-center bg-[rgba(12,14,10,0.65)] z-[1000]"
      role="presentation"
      onClick={() => close(null)}
    >
      <Card className="w-full max-w-lg" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <h3 className="m-0 mb-2.5 text-base">{state.title}</h3>
        <div className="grid grid-cols-2 gap-3 mb-[18px]">
          {state.fields.map((f) => (
            <label key={f.key} className="flex flex-col gap-1.5 text-xs text-rs-light-gray">
              {f.label}
              <Input
                type={f.type ?? "text"}
                value={values[f.key] ?? ""}
                onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
              />
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <Btn variant="ghost" onClick={() => close(null)}>Cancel</Btn>
          <Btn onClick={() => close(values)}>Save</Btn>
        </div>
      </Card>
    </div>
  );

  return { edit, dialog };
}
